import { LogLevel } from '../constants';
import { warnOnce } from './warn-once';

/**
 * Built-in leveled logger for request-iframe.
 *
 * - Default level is 'warn' (prints warn/error only)
 * - Trace/debug tools can raise to 'info' or 'trace'
 */

var DEBUG_PREFIX = '[request-iframe]';
var levelOrder = {
  [LogLevel.TRACE]: 10,
  [LogLevel.INFO]: 20,
  [LogLevel.WARN]: 30,
  [LogLevel.ERROR]: 40,
  [LogLevel.SILENT]: 100
};
var currentLevel = LogLevel.WARN;
export function getRequestIframeLogLevel() {
  return currentLevel;
}
export function setRequestIframeLogLevel(level) {
  currentLevel = level;
}

/**
 * Ensure current log level is at least as verbose as `level`.
 * - Example: current 'warn' + ensure 'trace' => becomes 'trace'
 */
export function ensureRequestIframeLogLevel(level) {
  if (levelOrder[level] < levelOrder[currentLevel]) {
    currentLevel = level;
  }
}
function shouldLog(level) {
  return levelOrder[level] >= levelOrder[currentLevel];
}

/**
 * Log with level gating + unified prefix.
 */
export function requestIframeLog(level, message, data) {
  if (currentLevel === LogLevel.SILENT) return;
  if (!shouldLog(level)) return;
  var timestamp = new Date().toISOString();
  var prefix = `${DEBUG_PREFIX} [${timestamp}] [${level.toUpperCase()}]`;
  var prefixStyle = 'font-weight:bold';
  var messageStyle = level === LogLevel.INFO ? 'color: #1976d2' : '';
  var method = level === LogLevel.TRACE ? 'debug' : level;
  if (data !== undefined) {
    console[method](`%c${prefix}%c ${message}`, prefixStyle, messageStyle, data);
  } else {
    console[method](`%c${prefix}%c ${message}`, prefixStyle, messageStyle);
  }
}

/**
 * Unified logger facade.
 *
 * Notes:
 * - This is a thin wrapper around existing functions (kept for backward compatibility).
 * - `once()` is useful for configuration/security hints to avoid noisy logs.
 */
export var logger = {
  getLevel: getRequestIframeLogLevel,
  setLevel: setRequestIframeLogLevel,
  ensureLevel: ensureRequestIframeLogLevel,
  trace(message, data) {
    requestIframeLog(LogLevel.TRACE, message, data);
  },
  info(message, data) {
    requestIframeLog(LogLevel.INFO, message, data);
  },
  warn(message, data) {
    requestIframeLog(LogLevel.WARN, message, data);
  },
  error(message, data) {
    requestIframeLog(LogLevel.ERROR, message, data);
  },
  /**
   * Log once by key (deduped across bundles/versions).
   */
  once(level, key, message, data) {
    warnOnce(key, () => requestIframeLog(level, message, data));
  }
};